import React, { useState } from 'react';

import { Styler, useDisclosure, useBreakpoints, inDOM, Container } from '@honeycomb/ui-core';
import { RootUspGroup } from '@honeycomb/data';
import { USPDialog } from '@honeycomb/ui';
import { TrBrandUSPItem } from './TrBrandUSPItem';

const CYCLE_INTERVAL = 4000;
const FADE_DURATION = 600;
const FADE_IN_DELAY = 400;

export type USPGroupProps = {
    uspGroup: RootUspGroup;
};

export function TrBrandUSPs({ uspGroup }: USPGroupProps): React.JSX.Element {
    const { uspItems: items } = uspGroup;
    const isClient = inDOM();

    const [selectedItemContentId, setSelectedItemContentId] = useState<number | null>(null);
    const [dialogOpen, { open: handleOpenDialog, close: handleCloseDialog }] = useDisclosure(false);
    const [dialogExited, setDialogExited] = React.useState<boolean>(false);
    const [dialogTitle, setDialogTitle] = React.useState<string>('');

    // const { backgroundColor, isBackgroundDark } = useResolveCMSColors({
    //     backgroundColorCMS: backgroundColorCMS || undefined,
    // });

    const handleItemClick = (contentId: string, title: string) => {
        if (contentId && Number(contentId) > 0) {
            setSelectedItemContentId(Number(contentId));
            setDialogTitle(title);
            setDialogExited(false);
            handleOpenDialog();
        }
    };

    // Rotation state
    const rotationTimeout = React.useRef<number | null>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const currentItemElementRef = React.useRef<HTMLElement | null>(null);

    const { largeUp } = useBreakpoints();
    const shouldRotate = !largeUp;

    const rotating = React.useRef<boolean>(shouldRotate);

    // Hide all item elements
    const hideAll = React.useCallback(() => {
        containerRef.current?.childNodes.forEach((element) => {
            const el = element as HTMLElement;
            el.style.display = 'none';
            el.style.opacity = '0';
            el.style.transition = '';
        });
    }, []);

    // Show all item elements
    const showAll = React.useCallback(() => {
        containerRef.current?.childNodes.forEach((element) => {
            const el = element as HTMLElement;
            el.style.display = '';
            el.style.opacity = '';
            el.style.transition = '';
        });
    }, []);

    // Rotate to next item
    const bumpItem = React.useCallback(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        const childElements = container.childNodes;
        const currentElement = currentItemElementRef.current;
        const firstElement = childElements[0] as HTMLElement;

        if (currentElement == null) {
            // Just display the first item without fading in
            firstElement.style.display = '';
            firstElement.style.opacity = '1';
            currentItemElementRef.current = firstElement;
        } else {
            // Remove incomplete transitions
            currentElement.style.display = '';
            currentElement.style.transition = '';
            currentElement.style.opacity = '';

            // We want to fade out the current element, hide it, then fade in the next element.
            const nextElement = (currentElement.nextElementSibling as HTMLElement) || firstElement;

            // Use setTimeout to fade in the next element
            window.setTimeout(() => {
                if (!shouldRotate) {
                    // The device width may have changed.
                    return;
                }

                // Reset the styling on the current element
                currentElement.style.display = 'none';
                currentElement.style.transition = '';

                // Un-hide the new item
                nextElement.style.display = '';
                nextElement.style.transition = `opacity ${FADE_DURATION}ms ease`;

                // Fade in the next element after a delay
                window.setTimeout(() => {
                    nextElement.style.opacity = '1';
                }, FADE_IN_DELAY);

                currentItemElementRef.current = nextElement;
            }, FADE_DURATION); // Wait for the fade-out to complete

            currentElement.style.transition = `opacity ${FADE_DURATION}ms ease`;
            currentElement.style.opacity = '0';

            currentItemElementRef.current = nextElement;
        }
    }, [shouldRotate]);

    const stopRotating = React.useCallback(() => {
        if (rotationTimeout.current) {
            window.clearTimeout(rotationTimeout.current);
            rotationTimeout.current = null;
        }

        hideAll();
        currentItemElementRef.current = null;
    }, [hideAll]);

    const startRotating = React.useCallback(() => {
        if (!isClient) {
            return;
        }

        stopRotating();

        // Display the first item and start the rotation loop
        const rotate = () => {
            bumpItem();
            rotationTimeout.current = window.setTimeout(rotate, CYCLE_INTERVAL);
        };

        bumpItem(); // Start by displaying the first item

        rotationTimeout.current = window.setTimeout(rotate, CYCLE_INTERVAL);
    }, [bumpItem, isClient, stopRotating]);

    // A useEffect to start or stop the rotation based on device width.
    React.useEffect(() => {
        if (shouldRotate) {
            rotating.current = true;
            startRotating();
        } else {
            // Cancel rotation
            stopRotating();
            rotating.current = false;
            showAll();
        }
    }, [shouldRotate, isClient, showAll, startRotating, stopRotating]);

    const handleVisibilityChange = React.useCallback(() => {
        // Stop rotating while the browser tab is hidden.
        if (rotating.current) {
            if (document.visibilityState === 'visible') {
                // Restart the cycle
                if (!rotationTimeout.current) {
                    startRotating();
                }
            } else {
                stopRotating();
            }
        }
    }, [startRotating, stopRotating]);

    React.useEffect(() => {
        // Mount
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            // Unmount
            if (rotationTimeout.current) {
                window.clearTimeout(rotationTimeout.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopRotating();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Styler component="section" styleId="usp-bar">
                <Container>
                    <Styler
                        ref={containerRef}
                        justifyContent={shouldRotate ? 'center' : 'space-between'}
                        display="flex"
                        alignItems="center"
                        gap={5}
                        mih={56}
                        py={3}
                    >
                        {items.map((item, index) => {
                            return (
                                <TrBrandUSPItem
                                    item={item}
                                    index={index}
                                    onItemClick={handleItemClick}
                                    isBackgroundDark={true}
                                    key={item.contentId}
                                />
                            );
                        })}
                    </Styler>
                </Container>
            </Styler>
            {(dialogOpen || !dialogExited) && (
                <USPDialog
                    dialogTitle={dialogTitle}
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    onExited={() => setDialogExited(true)}
                    additionalContentId={Number(selectedItemContentId)}
                />
            )}
        </>
    );
}
